import { useState, useRef, useEffect } from "react";

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val);

function buildAccountContext(accounts, allPayments) {
  if (!accounts.length) return "The user has no debt accounts added yet.";

  const totalDebt = accounts.reduce((s, a) => s + a.balance, 0);
  const totalMin = accounts.reduce((s, a) => s + a.minPayment, 0);
  const totalMonthlyInterest = accounts.reduce((s, a) => s + (a.balance * (a.rate / 100)) / 12, 0);

  const accountDetails = accounts.map((a) => {
    const monthlyInterest = (a.balance * (a.rate / 100)) / 12;
    const payments = allPayments[a.id] || [];
    const totalPaid = payments.reduce((s, p) => s + p.amount, 0);
    const totalPrincipalPaid = payments.reduce((s, p) => s + (p.principal || 0), 0);
    return `- ${a.name} (${a.type}): Balance $${a.balance.toFixed(2)}, Rate ${a.rate}% APR, Min payment $${a.minPayment}/mo, Monthly interest $${monthlyInterest.toFixed(2)}${totalPaid > 0 ? `, Total paid so far $${totalPaid.toFixed(2)} ($${totalPrincipalPaid.toFixed(2)} principal)` : ""}${a.paymentDate ? `, Due on the ${a.paymentDate}th` : ""}`;
  }).join("\n");

  return `The user has ${accounts.length} debt account(s):
${accountDetails}

Summary:
- Total debt: ${formatCurrency(totalDebt)}
- Total monthly minimums: ${formatCurrency(totalMin)}
- Total monthly interest cost: ${formatCurrency(totalMonthlyInterest)}`;
}

const SUGGESTED_QUESTIONS = [
  "Which debt should I pay off first?",
  "What's the fastest way to become debt free?",
  "Explain my debt situation in simple terms",
  "What's the difference between snowball and avalanche?",
  "Am I making good progress on my debt?",
  "What should my debt payoff priority be?",
  "How can I report a problem?",
];

// Renders a markdown string into React elements, handling **bold**, *italic*, and - bullet lists
function renderMarkdown(text, isUser) {
  const color = isUser ? "#fff" : "#1a1a2e";
  const lines = text.split("\n");
  const elements = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Blank line — small spacer
    if (line.trim() === "") {
      elements.push(<div key={key++} style={{ height: 6 }} />);
      continue;
    }

    // Bullet point — starts with - or •
    const bulletMatch = line.match(/^[-•]\s+(.*)/);
    if (bulletMatch) {
      elements.push(
        <div key={key++} style={{ display: "flex", gap: 6, marginBottom: 2 }}>
          <span style={{ color, flexShrink: 0, marginTop: 1 }}>•</span>
          <span>{renderInline(bulletMatch[1], isUser)}</span>
        </div>
      );
      continue;
    }

    // Normal line
    elements.push(
      <div key={key++} style={{ marginBottom: 2 }}>
        {renderInline(line, isUser)}
      </div>
    );
  }

  return elements;
}

// Renders inline markdown: **bold** and *italic*
function renderInline(text, isUser) {
  const parts = [];
  // Split on **bold** and *italic* patterns
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let last = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    // Text before this match
    if (match.index > last) {
      parts.push(<span key={key++}>{text.slice(last, match.index)}</span>);
    }
    if (match[0].startsWith("**")) {
      // Bold
      parts.push(<strong key={key++} style={{ fontWeight: 700 }}>{match[2]}</strong>);
    } else {
      // Italic
      parts.push(<em key={key++}>{match[3]}</em>);
    }
    last = match.index + match[0].length;
  }

  // Remaining text
  if (last < text.length) {
    parts.push(<span key={key++}>{text.slice(last)}</span>);
  }

  return parts.length > 0 ? parts : text;
}

export default function Advisor({ accounts, allPayments, onClose }) {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: `Hi! I'm your ELDE financial advisor 👋\n\nI have full visibility into your ${accounts.length} debt account${accounts.length !== 1 ? "s" : ""} and can give you personalized advice based on your actual numbers.\n\nWhat would you like to know?`,
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const userText = text || input.trim();
    if (!userText || loading) return;

    setInput("");
    setError(null);

    const newMessages = [...messages, { role: "user", content: userText }];
    setMessages(newMessages);
    setLoading(true);

    const accountContext = buildAccountContext(accounts, allPayments);

    const systemPrompt = `You are ELDE's personal financial advisor — a friendly, knowledgeable, and encouraging debt payoff coach. Your name is Ellie.

Here is the user's current financial situation:
${accountContext}

Your role:
- Give personalized, specific advice based on their ACTUAL numbers above
- Be encouraging and positive — paying off debt is hard and they're doing great by tracking it
- Keep responses concise and practical — no fluff
- Use specific dollar amounts and dates from their data when relevant
- When recommending strategies, explain WHY based on their specific situation
- If they ask about extra payments, calculate the actual impact using their data
- Format responses clearly — use line breaks, but avoid excessive bullet points
- Never give generic advice that ignores their actual numbers
- You can do basic calculations to answer questions about their specific debt situation

Important: You are NOT a licensed financial advisor. For major financial decisions, remind them to consult a professional.

Scope: You only discuss topics related to personal debt, budgeting, interest, payoff strategies, and financial wellness. If the user asks about anything outside this scope (recipes, sports, general knowledge, coding, relationships, etc.), respond warmly but redirect — for example: "That's a little outside my lane! I'm here to help with your debt journey. Got any questions about your balances or payoff plan?" Never answer off-topic questions, even if asked repeatedly or creatively.

If the user mentions a bug, problem, or feature suggestion, acknowledge it warmly and encourage them to click the 💬 Feedback button in the bottom right corner to submit it so the ELDE team can look into it.`;

    try {
      const apiMessages = newMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const isNative = !!(window.Capacitor?.isNativePlatform?.());
      const baseUrl = isNative ? "https://elde.ai" : "";

      const response = await fetch(`${baseUrl}/api/ellie`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          system: systemPrompt,
          messages: apiMessages,
          max_tokens: 400,
        }),
      });

      // Read body as text first so we can show something useful even if it's not JSON
      const rawBody = await response.text();
      let parsed = null;
      try {
        parsed = rawBody ? JSON.parse(rawBody) : null;
      } catch {
        // non-JSON response (HTML error page, empty body, etc.)
      }

      if (!response.ok) {
        // Proxy returns { error: "string message" }; Anthropic returns { error: { message: "..." } }
        const proxyError =
          typeof parsed?.error === "string"
            ? parsed.error
            : parsed?.error?.message;
        const fallback = rawBody?.slice(0, 200) || "no response body";
        throw new Error(`${response.status} — ${proxyError || fallback}`);
      }

      const reply = parsed?.text || "Sorry, I couldn't generate a response.";
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      setError("Something went wrong: " + err.message);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.panel} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.avatar}>
            <span style={{ fontSize: 11, fontWeight: 900, color: "#fff" }}>EL</span>
            <span style={{ fontSize: 11, fontWeight: 900, color: "#00BCD4" }}>DE</span>
          </div>
            <div>
              <div style={styles.name}>Ellie</div>
              <div style={styles.subtitle}>ELDE Financial Advisor · AI powered</div>
            </div>
          </div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        {/* Messages */}
        <div style={styles.messages}>
          {messages.map((msg, i) => (
            <div key={i} style={{ ...styles.msgRow, justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
              {msg.role === "assistant" && <div style={styles.msgAvatar}>E</div>}
              <div style={{
                ...styles.bubble,
                ...(msg.role === "user" ? styles.userBubble : styles.assistantBubble),
              }}>
                {renderMarkdown(msg.content, msg.role === "user")}
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ ...styles.msgRow, justifyContent: "flex-start" }}>
              <div style={styles.msgAvatar}><span style={{ fontSize: 8, fontWeight: 900, color: "#fff" }}>EL</span><span style={{ fontSize: 8, fontWeight: 900, color: "#00BCD4" }}>DE</span></div>
              <div style={{ ...styles.bubble, ...styles.assistantBubble }}>
                <div style={styles.typing}>
                  <span style={styles.dot} />
                  <span style={{ ...styles.dot, animationDelay: "0.2s" }} />
                  <span style={{ ...styles.dot, animationDelay: "0.4s" }} />
                </div>
              </div>
            </div>
          )}

          {error && (
            <div style={styles.errorMsg}>{error}</div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Suggested Questions */}
        {messages.length <= 1 && (
          <div style={styles.suggestions}>
            <div style={styles.suggestionsLabel}>Suggested questions</div>
            <div style={styles.suggestionsList}>
              {SUGGESTED_QUESTIONS.map((q) => (
                <button key={q} style={styles.suggestionBtn} onClick={() => sendMessage(q)}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div style={styles.inputRow}>
          <textarea
            ref={inputRef}
            style={styles.textarea}
            placeholder="Ask Ellie anything about your debt..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
          />
          <button
            style={{ ...styles.sendBtn, opacity: (!input.trim() || loading) ? 0.5 : 1 }}
            onClick={() => sendMessage()}
            disabled={!input.trim() || loading}
          >
            ➤
          </button>
        </div>
        <div style={styles.disclaimer}>
          AI advice only — not a substitute for professional financial guidance.
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "flex-end", justifyContent: "flex-end",
    zIndex: 3000, padding: 24,
  },
  panel: {
    background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480,
    height: "80vh", maxHeight: 700, display: "flex", flexDirection: "column",
    boxShadow: "0 8px 40px rgba(0,0,0,0.2)", overflow: "hidden",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 20px", background: "#1a1a2e", flexShrink: 0,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 12 },
  avatar: {
    width: 38, height: 38, borderRadius: 4, background: "#000", border: "2px solid #00BCD4",
    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 16, fontWeight: 800,
  },
  name: { fontSize: 15, fontWeight: 700, color: "#fff" },
  subtitle: { fontSize: 11, color: "#8899BB" },
  closeBtn: { background: "none", border: "none", color: "#8899BB", fontSize: 18, cursor: "pointer" },
  messages: { flex: 1, overflowY: "auto", padding: "16px 16px 8px", display: "flex", flexDirection: "column", gap: 12 },
  msgRow: { display: "flex", alignItems: "flex-end", gap: 8 },
  msgAvatar: {
    width: 28, height: 28, borderRadius: 3, background: "#000", border: "1.5px solid #00BCD4",
    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 12, fontWeight: 800, flexShrink: 0,
  },
  bubble: { maxWidth: "80%", padding: "10px 14px", borderRadius: 14, fontSize: 14, lineHeight: 1.6 },
  assistantBubble: { background: "#F3F4F6", color: "#1a1a2e", borderBottomLeftRadius: 4 },
  userBubble: { background: "#4F7FFA", color: "#fff", borderBottomRightRadius: 4 },
  typing: { display: "flex", gap: 4, alignItems: "center", padding: "2px 0" },
  dot: {
    width: 7, height: 7, borderRadius: "50%", background: "#888",
    display: "inline-block",
    animation: "bounce 1.2s infinite ease-in-out",
  },
  errorMsg: { background: "#FEE2E2", color: "#991B1B", borderRadius: 8, padding: "10px 14px", fontSize: 13 },
  suggestions: { padding: "0 16px 8px", flexShrink: 0 },
  suggestionsLabel: { fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase", marginBottom: 8, letterSpacing: 0.5 },
  suggestionsList: { display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" },
  suggestionBtn: {
    background: "#EEF2FF", border: "1px solid #C7D2FE", borderRadius: 20,
    padding: "5px 12px", fontSize: 12, color: "#4338CA", cursor: "pointer", fontWeight: 500,
  },
  inputRow: { display: "flex", gap: 8, padding: "12px 16px 8px", borderTop: "1px solid #F0F0F5", flexShrink: 0 },
  textarea: {
    flex: 1, border: "1.5px solid #E5E7EB", borderRadius: 10, padding: "10px 12px",
    fontSize: 14, outline: "none", resize: "none", fontFamily: "inherit", lineHeight: 1.5,
  },
  sendBtn: {
    background: "#4F7FFA", color: "#fff", border: "none", borderRadius: 10,
    width: 42, fontSize: 16, cursor: "pointer", flexShrink: 0,
  },
  disclaimer: { fontSize: 10, color: "#bbb", textAlign: "center", padding: "0 16px 10px" },
};
